/**
 * PATH: packages/email-hub/src/feature/reader-header/identity/IdentityBand.tsx
 * FILE_ID: EMAILHUB-READERHEADER-IDENTITY-BAND-001
 * PURPOSE: Render compact sender orientation with progressive recipient details.
 * ROLE IN ARCHITECTURE: Presentational leaf shared by preview and full readers.
 * MAIN EXPORTS: ReaderIdentityBand.
 * NON-RESPONSIBILITIES: CRM classification and provider mutations live elsewhere.
 * NOTES FOR FUTURE AI: Keep the resolved row compact; large recipient lists belong in disclosure.
 */
import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  EMAIL_ADDRESS_ATTR,
  RecipientList,
  getAvatarColor,
  getInitials,
} from '@quotech/ui'

import { cn } from '../../../_internal/lib/utils'
import type { Email } from '../../hooks/useEmails'
import { formatFullDate, formatRelativeDate } from '../../lib/utils'
import { ReceivingMailboxVia } from '../../components/RowAttributionChips'
import { WorkflowRoleChip } from '../../components/WorkflowRoleChip'
import { AVATAR } from '../tokens'

export interface ReaderIdentityBandProps {
  email: Email
  /**
   * Related-context affordances (Synced from / Other emails chips) shown
   * between the sender block and the recipient-details toggle.
   *
   * A SLOT, not a component: the band is a presentational leaf shared by
   * preview and full readers, and only the full reader's host knows whether
   * its context rail moved those sections up here (see ReaderRail).
   *
   * From 640px up it sits in the existing row (sender | chips | chevron).
   * Below that it wraps onto its own line (`order-3 w-full`): two labelled
   * pills plus Team plus a timestamp cannot share one 366px row without
   * painting through each other, which is what the operator reported
   * (BUG-2026-08-14-IDENTITY-ROW-SPILLS-ONTO-THE-CHIPS). The wrap costs
   * ~22px on a phone and keeps every label readable. See
   * phoneIdentityRow.test.tsx, which mounts the real chips in this slot.
   */
  related?: ReactNode
}

/** Deduped Inbox rows may carry per-copy mailboxes the base Email type lacks. */
type MailboxStampedEmail = Email & {
  mailboxes?: Array<{ email: string; name?: string | null }>
}

/** Meta-line "· via mailbox" segment; hidden entirely on unstamped legacy rows. */
function MetaMailbox({ email }: { email: MailboxStampedEmail }) {
  const mailboxKnown = Boolean(
    email.synced_by_mailbox_email ||
      email.mailboxes?.[0]?.email ||
      email.synced_by_email,
  )
  if (!mailboxKnown) return null
  return (
    <>
      <span aria-hidden>·</span>
      <ReceivingMailboxVia email={email} />
    </>
  )
}

export function ReaderIdentityBand({
  email,
  related,
}: ReaderIdentityBandProps) {
  const [expanded, setExpanded] = useState(false)
  const senderName =
    email.from_name ||
    email.from_address?.split('@')[0] ||
    'Unknown sender'
  const hasMoreRecipients =
    Boolean(email.cc_addresses?.length) || Boolean(email.bcc_addresses?.length)

  return (
    <section
      className="border-b border-border/50 bg-background px-3 py-1.5"
      aria-label="Message identity"
      data-testid="reader-identity-band"
    >
      <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white',
            getAvatarColor(email.from_name || email.from_address || ''),
          )}
          style={{ height: AVATAR, width: AVATAR }}
          aria-hidden
        >
          {getInitials(senderName)}
        </span>
        {/* overflow-hidden is the guarantee, not the layout. This block is
            `flex-1 min-w-0`, so the row is free to squeeze it to nothing to
            keep the shrink-0 chips and toggle whole — and anything inside it
            that cannot shrink then spills OUT of the block and lands on those
            chips. Clipping here bounds that blast radius; the two changes
            below (a shrinkable role chip, a short date) are what make the
            content actually fit so the clip never has to engage.
            Safe to clip: every descendant is a plain span or a truncating
            run. Neither the role chip nor the mailbox stamp opens a menu, and
            the chips' own panels are portalled by AdaptiveOverlay. */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              className="truncate text-[13px] font-semibold text-foreground"
              data-testid="reader-identity-sender"
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
              // The related-context chips beside this block are shrink-0, so a
              // long name ellipses rather than pushing them out of the band.
              // The full value has to stay reachable, the way the address next
              // to it already is.
              title={senderName}
            >
              {senderName}
            </span>
            <span
              className="hidden min-w-0 truncate text-[11px] text-muted-foreground min-[560px]:inline"
              title={email.from_address}
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
            >
              &lt;{email.from_address}&gt;
            </span>
            {/* Truncate its own label rather than push the sender line wide.
                The chip ships shrink-0 with max-w-[140px] for the email rows,
                which is 140px this line does not have on a phone. */}
            <WorkflowRoleChip email={email} className="min-w-0 shrink" />
          </div>
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground [&_[data-testid=email-receiving-mailbox]]:min-w-0 [&_[data-testid=email-receiving-mailbox]]:shrink [&_[data-testid=email-receiving-mailbox]]:truncate">
            {/* Shrinkable, like the mailbox chip beside it (see the arbitrary
                selector on this row). Without it the recipient run keeps its
                content width — a flex item's automatic minimum — so a long
                "To" pushed the meta line past the band on a narrow reader
                instead of ellipsing. */}
            <RecipientList
              label="To"
              recipients={email.to_addresses}
              emptyText="me"
              max={2}
              className="min-w-0 shrink truncate"
            />
            <span aria-hidden>·</span>
            {/* The date stays shrink-0 — a half-ellipsed timestamp is worse
                than a shorter one — so on a phone it renders the SHORT form
                rather than giving width. "Jul 30, 2026, 4:56 AM" is ~130px at
                11px, more than this whole block gets beside the chips, and
                being shrink-0 it overflowed instead of yielding. The full
                value stays on the title, in the recipient disclosure, and on
                the message row directly below. Threshold matches the
                from-address above it: both arrive together at 560px. */}
            <span className="shrink-0" title={formatFullDate(email.received_at)}>
              <span className="min-[560px]:hidden">
                {formatRelativeDate(email.received_at)}
              </span>
              <span className="hidden min-[560px]:inline">
                {formatFullDate(email.received_at)}
              </span>
            </span>
            <MetaMailbox email={email} />
          </div>
        </div>
        {related ? (
          <span
            data-testid="reader-identity-related"
            className="order-3 flex w-full min-w-0 items-center gap-1 pl-10 min-[640px]:order-none min-[640px]:w-auto min-[640px]:pl-0"
          >
            {related}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className={cn(
            'relative order-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground min-[640px]:order-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
            // 28px control in the fixed identity band — pseudo-element grows
            // the coarse-pointer target without overflowing the band (WORK ITEM 7).
            "before:absolute before:inset-0 before:content-[''] pointer-coarse:before:inset-[-8px]",
          )}
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide recipient details' : 'Show recipient details'}
          title={expanded ? 'Hide details' : 'Show recipients and mailbox details'}
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-150 motion-reduce:transition-none',
              expanded && 'rotate-180',
            )}
          />
        </button>
      </div>
      {expanded && (
        <div
          className="ml-10 mt-1 grid gap-0.5 border-l border-border/50 pl-2 text-[11px] text-muted-foreground"
          data-testid="reader-identity-details"
        >
          <div {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}>
            {email.from_address}
          </div>
          <RecipientList
            label="To"
            recipients={email.to_addresses}
            emptyText="me"
          />
          {hasMoreRecipients && Boolean(email.cc_addresses?.length) && (
            <RecipientList label="Cc" recipients={email.cc_addresses} />
          )}
          {hasMoreRecipients && Boolean(email.bcc_addresses?.length) && (
            <RecipientList label="Bcc" recipients={email.bcc_addresses} />
          )}
        </div>
      )}
    </section>
  )
}
